import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { WindReveal } from "@/components/Cinematic";

const STAGES = [
  { k: "Seed", n: "01", d: "It starts with intent. We define the strategic seed — who the brand is for, why it exists, and what it must achieve commercially." },
  { k: "Roots", n: "02", d: "Research grounds everything. Audience, market, category and craft — deep roots so the brand can't be blown over by a trend." },
  { k: "Growth", n: "03", d: "Identity, systems and design take shape. Logo, type, colour, packaging, collections — a coherent living system built to scale." },
  { k: "Bloom", n: "04", d: "The brand meets the world. Campaigns, launches and touchpoints choreographed for the moment of first impression." },
  { k: "Harvest", n: "05", d: "We measure what matters. Revenue, engagement, sell-through — design proven on the balance sheet." },
];

function Stage({ s, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const grow = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return (
    <div ref={ref} className="grid md:grid-cols-[120px_1fr] gap-8 items-start py-14 border-t border-[#81754B]/25">
      <div className="flex md:flex-col items-center gap-4">
        <span className="font-serif text-5xl text-[#B4833D]">{s.n}</span>
        <svg width="30" height="80" viewBox="0 0 30 80" className="hidden md:block" aria-hidden="true">
          <motion.path d={`M15 80 C15 60 5 55 15 30 C25 12 15 5 15 0`} stroke="#81754B" strokeWidth="2" fill="none"
            style={{ pathLength: grow }} />
          <motion.circle cx="15" cy="6" r="4" fill="#66371B" style={{ scale: grow }} />
        </svg>
      </div>
      <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.7 }}>
        <h2 className="font-serif text-4xl lg:text-6xl text-[#3F3F2C]">{s.k}</h2>
        <p className="mt-4 max-w-2xl text-lg text-[#3F3F2C] leading-relaxed">{s.d}</p>
      </motion.div>
    </div>
  );
}

export default function Process() {
  useEffect(() => { document.title = "Process — seedesign"; }, []);
  return (
    <div className="pt-28 pb-24">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <p className="text-sm uppercase tracking-[0.2em] text-[#66371B] mb-4">Process</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#3F3F2C] max-w-4xl leading-[0.95]">
          <WindReveal text="A brand grows like a tree." />
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#3F3F2C]">
          Five seasons from seed to harvest — a process designed for brands meant to last.
        </p>
        <div className="mt-16">
          {STAGES.map((s, i) => <Stage key={s.k} s={s} i={i} />)}
        </div>
      </div>
    </div>
  );
}
